import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "../context/LangContext";
import { useCurrency } from "../context/CurrencyContext";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { propertyTypes } from "../lib/i18n";
import { Plus, Building2, CheckCircle2, Clock, MapPin, X, CreditCard } from "lucide-react";

export default function Properties() {
  const { t, lang } = useLang();
  const { code: ccy } = useCurrency();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [locations, setLocations] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", type: "chalet", governorate: "", wilayat: "", village: "",
    description: "", daily_rate: 0, hourly_rate: 0,
  });

  const load = async () => {
    try {
      const [p, l] = await Promise.all([api.get("/properties"), api.get("/oman/locations")]);
      setProperties(p.data);
      setLocations(l.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const wilayatsList = form.governorate ? locations[form.governorate] : null;
  const wilayatsEn = wilayatsList?.wilayats || [];
  const wilayats = wilayatsList
    ? (lang === "ar" ? (wilayatsList.wilayats_ar || wilayatsList.wilayats) : wilayatsList.wilayats)
    : [];

  const planMax = user?.plan === "premium" ? 3 : 1;
  const atLimit = properties.length >= planMax;

  const handleAddClick = () => {
    if (atLimit) {
      toast.info(user?.plan === "premium" ? t("property_limit_reached") : t("payment_required_add_property"));
      navigate("/pay?reason=extra");
      return;
    }
    setShowAdd(true);
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!form.name || !form.type || !form.governorate || !form.wilayat) return toast.error(t("fields_required"));
    setSaving(true);
    try {
      await api.post("/properties", { ...form, daily_rate: Number(form.daily_rate) || 0, hourly_rate: Number(form.hourly_rate) || 0 });
      toast.success(t("saved"));
      setShowAdd(false);
      setForm({ name: "", type: "chalet", governorate: "", wilayat: "", village: "", description: "", daily_rate: 0, hourly_rate: 0 });
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
      if (err?.response?.status === 402) {
        setShowAdd(false);
        setTimeout(() => navigate("/pay?reason=extra"), 800);
      }
    } finally { setSaving(false); }
  };

  const govLabel = (key) => lang === "ar" ? (locations[key]?.ar || key) : key;
  const wilayatLabel = (govKey, wilKey) => {
    const loc = locations[govKey];
    if (!loc) return wilKey;
    if (lang !== "ar") return wilKey;
    const idx = loc.wilayats?.indexOf(wilKey);
    return idx > -1 ? (loc.wilayats_ar?.[idx] || wilKey) : wilKey;
  };

  return (
    <div className="space-y-5" data-testid="properties-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold flex items-center gap-2">
            <Building2 className="text-[#2A5948]" size={26} />
            {t("properties_page")}
          </h1>
          <p className="text-sm text-[#5C6053] mt-1">{t("all_properties")}</p>
        </div>
        <button
          data-testid="add-property-btn"
          onClick={handleAddClick}
          className="brand-btn px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2"
        >
          <Plus size={16} /> {t("add_property")}
        </button>
      </div>

      {atLimit && (
        <div data-testid="subscribe-notice" className="rounded-2xl border border-[#E5B25D]/60 bg-[#FFF7E3] p-4 text-sm text-[#8A6D1F] flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#E5B25D]/30 flex items-center justify-center flex-shrink-0"><CreditCard size={16} /></div>
          <div className="flex-1">{user?.plan === "premium" ? t("property_limit_reached") : t("payment_required_add_property")}</div>
          <Link data-testid="notice-pay-btn" to="/pay?reason=extra" className="px-3 py-1.5 rounded-lg brand-btn text-xs">{t("subscribe_now")}</Link>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {properties.map((p) => (
          <Link
            key={p.id}
            to={`/app/property/${p.id}`}
            data-testid={`property-card-${p.id}`}
            className="bg-white border border-[#E5E1D8] rounded-2xl p-5 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all block"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-lg truncate">{p.name}</h3>
                  <span className="text-xs px-2 py-0.5 rounded bg-[#F3EFE7] text-[#5C6053]">{t(`type_${p.type}`)}</span>
                </div>
                <div className="mt-2 text-sm text-[#5C6053] flex items-center gap-1">
                  <MapPin size={12} /> {govLabel(p.governorate)} — {wilayatLabel(p.governorate, p.wilayat)}
                </div>
              </div>
              {p.is_approved ? (
                <span data-testid={`badge-approved-${p.id}`} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[#E6F1E7] text-[#2A5948]">
                  <CheckCircle2 size={12} /> {t("approved")}
                </span>
              ) : (
                <span data-testid={`badge-pending-${p.id}`} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[#FFF3D6] text-[#8A6D1F]">
                  <Clock size={12} /> {t("pending_approval")}
                </span>
              )}
            </div>
          </Link>
        ))}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1C1E17]/50 backdrop-blur-sm" onClick={() => setShowAdd(false)}>
          <form onSubmit={submitAdd} className="w-full max-w-lg bg-white rounded-2xl p-6 shadow-2xl border border-[#E5E1D8] max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="add-property-form">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{t("add_property")}</h3>
              <button type="button" onClick={() => setShowAdd(false)} className="p-1 rounded hover:bg-[#F3EFE7]"><X size={18} /></button>
            </div>
            {atLimit && (
              <div className="mt-3 text-xs text-[#8A6D1F] bg-[#FFF7E3] border border-[#E5B25D]/40 rounded-lg px-3 py-2">
                {user?.plan === "premium" ? t("property_limit_reached") : t("payment_required_add_property")}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              <Field label={t("property_name")}>
                <input data-testid="new-prop-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="inp" />
              </Field>
              <Field label={t("property_type")}>
                <select data-testid="new-prop-type" value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} className="inp">
                  {propertyTypes.map((pt) => <option key={pt} value={pt}>{t(`type_${pt}`)}</option>)}
                </select>
              </Field>
              <Field label={t("governorate")}>
                <select data-testid="new-prop-governorate" value={form.governorate} onChange={(e) => setForm({...form, governorate: e.target.value, wilayat: ""})} className="inp">
                  <option value="">--</option>
                  {Object.entries(locations).map(([k, v]) => <option key={k} value={k}>{lang === "ar" ? v.ar : k}</option>)}
                </select>
              </Field>
              <Field label={t("wilayat")}>
                <select data-testid="new-prop-wilayat" value={form.wilayat} onChange={(e) => setForm({...form, wilayat: e.target.value})} className="inp" disabled={!form.governorate}>
                  <option value="">--</option>
                  {wilayatsEn.map((w, i) => <option key={w} value={w}>{wilayats[i] || w}</option>)}
                </select>
              </Field>
              <Field label={t("village")}>
                <input data-testid="new-prop-village" value={form.village} onChange={(e) => setForm({...form, village: e.target.value})} className="inp" />
              </Field>
              <Field label={`Daily rate (${ccy})`}>
                <input type="number" data-testid="new-prop-daily" value={form.daily_rate} onChange={(e) => setForm({...form, daily_rate: e.target.value})} className="inp" />
              </Field>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setShowAdd(false)} className="px-3 py-2 rounded-lg text-sm border border-[#E5E1D8]">{t("cancel")}</button>
              <button data-testid="new-prop-submit" disabled={saving} type="submit" className="brand-btn px-4 py-2 rounded-lg text-sm disabled:opacity-60">{saving ? "..." : t("save")}</button>
            </div>
            <style>{`.inp { width: 100%; border: 1px solid #E5E1D8; background: #F9F6F0; border-radius: 10px; padding: 8px 12px; font-size: 14px; outline: none; }
              .inp:focus { box-shadow: 0 0 0 2px rgba(42,89,72,0.25); border-color: #2A5948; }`}</style>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-[#5C6053] mb-1">{label}</div>
      {children}
    </label>
  );
}
